import { Box, Collapse } from '@mui/material';
import AddressLookup from '@acorn-portal/shared/src/components/addressLookup';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import ToggleButtonFormField from '@acorn-portal/shared/src/components/inputs/toggle-button-form-field';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import RadioGroupButton from '@acorn-portal/shared/src/components/inputs/radio-button-group';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import { Form, Formik } from 'formik';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import useNoChangeWarning from '@acorn-portal/shared/src/hooks/useNoChangeWarning';
import useGetOrCreateMidTermAdjustment, {
	handleChangeOfAddressRating,
} from '@acorn-portal/shared/src/models/midTermAdjustment-model';
import { useDiscardMta } from '@acorn-portal/shared/src/hooks/use-discard-mta';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Address, MtaChangeType } from '@acorn-portal/shared/src/services/api';
import { updateMidTermAdjustmentDetails } from '@acorn-portal/shared/src/store/midTermAdjustment.slice';
import { setMidTermAdjustmentRatingResponse } from '@acorn-portal/shared/src/store/midTermAdjustmentRatingResponse.slice';
import { isTelematics, isUsedForBusiness } from '@acorn-portal/shared/src/utils';

import {
	ANNUAL_MILEAGE,
	BUSINESS_MILEAGE,
	OVERNIGHT_ADDRESS_DIFFERENT,
} from '@acorn-portal/shared/src/utils/modalText';
import getClosestMileage from '@acorn-portal/shared/src/utils/getClosestMileage';
import { Product } from '@acorn-portal/shared/src/services/interface/Shared';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import AutoCompleteFormField from '@acorn-portal/shared/src/components/inputs/AutoCompleteFormField';
import { updateChangeOfAddress } from '../../store/renewalChanges.slice';

import ChangeOfAddressSchema from '../../utils/validation/ChangeOfAddressSchema';
import useGetMileageOptions from '../../hooks/useGetMileageOptions';
import hasOverNightAddress from '../../utils/has-overnight-address';
import { checkFormConditions } from './utils';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';
export interface IFormChangeAddress {
	isHomeOwner: string;
	address: Address;
	annualMileage: string;
	businessMileage: string;
	licencingAuthority?: string;
	operatingArea?: string;
	keptOvernight: string;
	overnightAddress: Address;
	overnightAddressIsAway: string;
	template: string;
}

const ChangeOfAddress: FC = () => {
	const [open, setOpen] = useState(false);
	const navigate = useNavigate();
	const mtaChanges = useAppSelector((redux) => redux.midTermAdjustment);
	const activePolicyDetails = useAppSelector((redux) => redux.midTermAdjustmentAPI);
	const lists = useAppSelector((redux) => redux.referenceData);
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);

	const { annualMileageOptions, getBusinessMileageOptions } = useGetMileageOptions();

	const dispatch = useAppDispatch();
	const [loading, setLoading] = useState(false);
	const mtaResponse = useAppSelector((redux) => redux.midTermAdjustmentResponse);
	const [hasChanged] = useNoChangeWarning<IFormChangeAddress>();
	const { track } = useTrack();
	// Track which fields have been touched by the user

	const { midTermAdjustment } = useGetOrCreateMidTermAdjustment({
		changeType: MtaChangeType.CHANGE_ADDRESS,
		setAdjustmentLoading: setLoading,
	});

	const { discardLoading } = useDiscardMta();

	const isTelematicsPolicy = isTelematics(fullPolicy.product);

	const isMyPolicy = fullPolicy.product === 'TELEMATICS' && import.meta.env.VITE_AUTH_REALM === 'MyPolicy';

	const isTaxi = fullPolicy.product === Product.TAXI;

	useEffect(() => {
		if (!mtaChanges.policyReference) {
			navigate(ROUTE_PATHS.home);
		}
	}, []);

	useEffect(() => {
		track(`Change Of Address Page Viewed`);
	}, []);

	if (loading)
		return (
			<JourneyCardLayout pageTitle='' loading={loading || discardLoading}>
				<div />
			</JourneyCardLayout>
		);

	const initialValues: IFormChangeAddress = {
		isHomeOwner: midTermAdjustment.policyDetails.policyholder.isHomeOwner ? 'yes' : 'no',
		address: {
			...midTermAdjustment.policyDetails.policyholder.address,
			line2: midTermAdjustment.policyDetails.policyholder.address.line2,
			line3: midTermAdjustment.policyDetails.policyholder.address.line3,
			line4: midTermAdjustment.policyDetails.policyholder.address.line4,
		},
		licencingAuthority:
			midTermAdjustment.vehicleDetails.licencingAuthority ?? fullPolicy.vehicleDetails.licencingAuthority ?? '',
		operatingArea: midTermAdjustment.policyDetails.operatingArea ?? fullPolicy.coverDetails.operatingArea ?? '',
		overnightAddress: {
			...(midTermAdjustment.vehicleDetails.overnightAddress || {
				line1: '',
				line2: '',
				line3: '',
				line4: '',
				city: '',
				postcode: '',
				country: '',
				county: '',
			}),
		},
		annualMileage: getClosestMileage(annualMileageOptions, midTermAdjustment.vehicleDetails.annualMileage),
		businessMileage: midTermAdjustment.vehicleDetails.businessMileage?.toString() || '',
		keptOvernight: midTermAdjustment.vehicleDetails.vehicleOvernight || '',
		overnightAddressIsAway: hasOverNightAddress(
			midTermAdjustment.policyDetails.policyholder.address,
			midTermAdjustment.vehicleDetails.overnightAddress ?? ({} as Address),
		)
			? 'yes'
			: 'no',
		template: '',
	};

	const handleOnSubmit = async (values: IFormChangeAddress) => {
		const newMTAChanges = values;

		const activePolicyValues = {
			isHomeOwner: activePolicyDetails.policyDetails.policyholder.isHomeOwner ? 'yes' : 'no',
			address: {
				...activePolicyDetails.policyDetails.policyholder.address,
				line2: activePolicyDetails.policyDetails.policyholder.address.line2,
				line3: activePolicyDetails.policyDetails.policyholder.address.line3,
				line4: activePolicyDetails.policyDetails.policyholder.address.line4,
			},
			licencingAuthority: activePolicyDetails.vehicleDetails.licencingAuthority ?? '',
			operatingArea: activePolicyDetails.policyDetails.operatingArea ?? '',
			overnightAddress: activePolicyDetails.vehicleDetails.overnightAddress || {
				line1: '',
				line2: '',
				line3: '',
				line4: '',
				city: '',
				country: '',
				postcode: '',
			},
			annualMileage: getClosestMileage(annualMileageOptions, activePolicyDetails.vehicleDetails.annualMileage),
			businessMileage: activePolicyDetails.vehicleDetails.businessMileage?.toString() || '',
			keptOvernight: activePolicyDetails.vehicleDetails.vehicleOvernight,
			overnightAddressIsAway: hasOverNightAddress(
				activePolicyDetails.policyDetails.policyholder.address,
				activePolicyDetails.vehicleDetails.overnightAddress ?? ({} as Address),
			)
				? 'yes'
				: 'no',
			template: '',
		};

		const { updateSection, continueToRate, navigateToSummary } = checkFormConditions(
			hasChanged(activePolicyValues, newMTAChanges),
			hasChanged(initialValues, newMTAChanges),
			mtaResponse,
			mtaChanges.changeOfAddress,
		);

		if (!continueToRate && !navigateToSummary) {
			return;
		}
		if (!continueToRate && navigateToSummary) {
			navigate(ROUTE_PATHS.makeAChangeSummary);
			return;
		}

		setLoading(true);

		let overnightAddress = {
			line1: values.overnightAddress.line1,
			line2: values.overnightAddress.line2 ?? undefined,
			line3: values.overnightAddress.line3 ?? undefined,
			line4: values.overnightAddress.line4 ?? undefined,
			city: values.overnightAddress.city,
			postcode: values.overnightAddress.postcode,
		};
		if (values.overnightAddressIsAway === 'no') {
			overnightAddress = {
				line1: values.address.line1,
				line2: values.address.line2 ?? undefined,
				line3: values.address.line3 ?? undefined,
				line4: values.address.line4 ?? undefined,
				city: values.address.city,
				postcode: values.address.postcode,
			};
		}

		try {
			const result = await handleChangeOfAddressRating({
				midTermAdjustment,
				values: {
					...values,
					overnightAddress,
				},
				product: fullPolicy.product,
			});

			dispatch(
				updateMidTermAdjustmentDetails({
					...midTermAdjustment,
					changeOfAddress: updateSection,
					vehicleDetails: {
						...midTermAdjustment.vehicleDetails,
						annualMileage: values.annualMileage ? parseInt(values.annualMileage, 10) : 0,
						businessMileage: values.businessMileage ? parseInt(values.businessMileage.toString(), 10) : 0,
						vehicleOvernight: values.keptOvernight,
						overnightAddress,
						licencingAuthority: values.licencingAuthority,
					},
					policyDetails: {
						...midTermAdjustment.policyDetails,
						policyholder: {
							address: {
								postcode: values.address.postcode,
								line1: values.address.line1 ?? '',
								line2: values.address.line2,
								line3: values.address.line3,
								line4: values.address.line4,
								city: values.address.city,
								county: values.address.county,
								country: values.address.country ?? '',
							},
							isHomeOwner: values.isHomeOwner === 'yes',
						},
						operatingArea: values.operatingArea ?? '',
					},
				}),
			);

			if (!result) {
				throw new Error('Rating response is undefined');
			}

			dispatch(setMidTermAdjustmentRatingResponse(result));
			if (result.hasDeclined) {
				navigate(ROUTE_PATHS.makeAChangeReferral);
				return;
			}
			setLoading(false);
			dispatch(updateChangeOfAddress(updateSection));
			navigate(ROUTE_PATHS.makeAChangeSummary);
		} catch (error) {
			console.error('Error during change of address rating:', error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	};

	return (
		<JourneyCardLayout pageTitle='Change Of Address' loading={loading || discardLoading}>
			<Formik<IFormChangeAddress>
				initialValues={initialValues}
				validationSchema={ChangeOfAddressSchema(midTermAdjustment, fullPolicy)}
				onSubmit={handleOnSubmit}
				key={JSON.stringify(midTermAdjustment.transactionId)}
			>
				{(formik) => {
					return (
						<Form>
							<FormikLogErrors />
							<AddressLookup
								initialValue={formik.values.address}
								onChange={(value) => {
									formik.setFormikState((formState) => ({
										...formState,
										values: {
											...formState.values,
											address: {
												...formState.values.address,
												line1: value?.line1 ?? '',
												line2: value?.line2,
												line3: value?.line3,
												line4: value?.line4,
												city: value?.city ?? '',
												county: value?.county,
												country: value?.country ?? '',
												postcode: value?.postcode ?? '',
											},
										},
									}));
								}}
								globalError={formik.touched.address?.line1 && Boolean(formik.errors.address?.line1)}
							/>
							{!isTaxi && !isTelematicsPolicy && !isMyPolicy && (
								<>
									<SelectFormField
										label='Annual mileage'
										name='annualMileage'
										id='annualMileage'
										value={formik.values.annualMileage}
										onChange={(value) => {
											formik.setFieldValue('annualMileage', value.toString());
										}}
										options={annualMileageOptions}
										modal={{
											title: ANNUAL_MILEAGE.header,
											description: ANNUAL_MILEAGE.text,
										}}
										error={formik.touched.annualMileage && Boolean(formik.errors.annualMileage)}
										helperText={formik.touched.annualMileage && formik.errors.annualMileage}
									/>
									<Collapse in={isUsedForBusiness(midTermAdjustment.vehicleDetails.vehicleUse)}>
										<Box>
											<SelectFormField
												label='Business mileage'
												name='businessMileage'
												id='businessMileage'
												value={formik.values.businessMileage}
												onChange={(value) => {
													formik.setFieldValue('businessMileage', value.toString());
												}}
												options={getBusinessMileageOptions(formik.values.annualMileage)}
												modal={{
													title: BUSINESS_MILEAGE.header,
													description: BUSINESS_MILEAGE.text,
												}}
												error={formik.touched.businessMileage && Boolean(formik.errors.businessMileage)}
												helperText={formik.touched.businessMileage && formik.errors.businessMileage}
											/>
										</Box>
									</Collapse>
								</>
							)}
							{isTaxi && (
								<>
									<AutoCompleteFormField
										label='Please select your licensing authority'
										name='licencingAuthority'
										id='licencingAuthority'
										value={formik.values.licencingAuthority}
										onChange={(value) => {
											formik.setFieldValue('licencingAuthority', value);
										}}
										options={lists.licencingArea as IOptions[]}
										error={formik.touched.licencingAuthority && Boolean(formik.errors.licencingAuthority)}
										helperText={formik.touched.licencingAuthority && formik.errors.licencingAuthority}
									/>
									<AutoCompleteFormField
										label='Primary operating area'
										name='operatingArea'
										id='operatingArea'
										value={formik.values.operatingArea}
										onChange={(value) => {
											formik.setFieldValue('operatingArea', value);
										}}
										options={lists.licencingArea as IOptions[]}
										error={formik.touched.operatingArea && Boolean(formik.errors.operatingArea)}
										helperText={formik.touched.operatingArea && formik.errors.operatingArea}
									/>
								</>
							)}
							<ToggleButtonFormField
								label='Where is the vehicle kept overnight?'
								name='keptOvernight'
								id='keptOvernight'
								value={formik.values.keptOvernight}
								onChange={(value) => {
									formik.setFieldValue('keptOvernight', value);
								}}
								options={lists.vehicleStorageOptions as IOptions[]}
								error={formik.touched.keptOvernight && Boolean(formik.errors.keptOvernight)}
								helperText={formik.touched.keptOvernight && formik.errors.keptOvernight}
							/>
							<RadioGroupButton
								label='Is your home address different to the address where your vehicle is kept overnight?'
								name='overnightAddressIsAway'
								id='overnightAddressIsAway'
								value={formik.values.overnightAddressIsAway}
								onChange={(value) => {
									formik.setFieldValue('overnightAddressIsAway', value);

									if (value === 'no') {
										formik.setFieldValue('overnightAddress', { ...formik.values.address });
									}
								}}
								error={formik.touched.overnightAddressIsAway && Boolean(formik.errors.overnightAddressIsAway)}
								helperText={formik.touched.overnightAddressIsAway && formik.errors.overnightAddressIsAway}
								modal={{
									title: OVERNIGHT_ADDRESS_DIFFERENT.title,
									description: OVERNIGHT_ADDRESS_DIFFERENT.description,
								}}
							/>
							<Collapse in={formik.values.overnightAddressIsAway === 'yes'}>
								<AddressLookup
									initialValue={formik.values.overnightAddress}
									firstFieldLabel='Overnight House number or Name'
									postcodeLabel='Overnight Postcode'
									onChange={(value) => {
										formik.setFieldValue('overnightAddress', {
											...formik.values.overnightAddress,
											line1: value?.line1 ?? '',
											line2: value?.line2,
											line3: value?.line3,
											line4: value?.line4,
											city: value?.city ?? '',
											county: value?.county,
											country: value?.country ?? '',
											postcode: value?.postcode ?? '',
										});
									}}
								/>
							</Collapse>
							<RadioGroupButton
								label='Are you a homeowner?'
								name='isHomeOwner'
								id='isHomeOwner'
								value={formik.values.isHomeOwner}
								onChange={(value) => {
									formik.setFieldValue('isHomeOwner', value);
								}}
								error={formik.touched.isHomeOwner && Boolean(formik.errors.isHomeOwner)}
								helperText={formik.touched.isHomeOwner && formik.errors.isHomeOwner}
							/>
							{formik.errors.template && (
								<Box color='error.main' mb={2} sx={{ textAlign: 'right' }}>
									{formik.errors.template}
								</Box>
							)}
							<ActionButtons>
								<BackButton onClick={() => setOpen(true)}>Back</BackButton>
								<ContinueButton type='submit'>Continue</ContinueButton>
							</ActionButtons>
							<HelperModal
								open={open}
								onClose={() => {
									setOpen(false);
								}}
								title='Discard The Changes?'
								description='Are you sure you wish to discard the changes?'
								closeText='No'
								submitText='Yes'
								onSubmit={() => {
									formik.resetForm();
									navigate(-1);
								}}
							/>
						</Form>
					);
				}}
			</Formik>
		</JourneyCardLayout>
	);
};

export default ChangeOfAddress;
